import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Layout,
  Menu,
  Typography,
  Space,
  Grid,
  Button,
  Divider,
} from "antd";
import {
  UserOutlined,
  DashboardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  WalletOutlined,
  ApiOutlined,
  TeamOutlined,
  CalendarOutlined,  
  CheckSquareOutlined,  
  FormOutlined,  
  SettingOutlined,  
  SafetyCertificateOutlined,
  FileOutlined,
  EuroCircleOutlined,
} from "@ant-design/icons";
import Dashboard from "../components/admin/Dashboard";
import Categories from "../components/admin/Categories";
import Users from "../components/admin/Users";
import Dumps from "../components/admin/Dump";
import Logo from "../components/common/Logo";
import LoadingScreen from "../components/loadingScreen/LoadingScreen";
import useAuthStore from '../stores/authStore';
import { jwtDecode } from "jwt-decode";
import UserMenu from "../components/common/UserMenu";
import Employee from "../components/admin/Employee";
import Bill from "../components/admin/Bill";  
import Platforms from "../components/admin/Platforms";  
import Customers from "../components/admin/Customers";  
import Bookings from "../components/admin/Bookings";  
import Todos from "../components/admin/Todos";  
import Alloggiati from "../components/admin/Alloggiati";  
import Certificati from "../components/admin/Certificati";  
import MediaManager from "../components/admin/MediaManager";
import AirBNBCalendar from "../components/admin/AirBNBCalendar";
import ImpostaSoggiorno from "../components/admin/ImpostaSoggiorno";

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

// Custom ItemGroup component that shows either the title or a separator based on collapsed state
const CustomItemGroup = ({ title, collapsed, children }) => {
  if (collapsed) {
    return (
      <>
        <Divider style={{ margin: "8px 0", opacity: 0.5 }} />
        {children}
      </>
    );
  } else {
    return (
      <Menu.ItemGroup key={title} title={title}>
        {children}
      </Menu.ItemGroup>
    );
  }
};

const AdminDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({
    email: "",
    username: "",
    is_staff: false,
    Email: "",
    Name: "",
    Surname: "",
    Gender: "",
  });
  const BASE_URL = process.env.PUBLIC_URL;
  const logout = useAuthStore(state => state.logout);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserData({
          email: decoded.email,
          username: decoded.email.split('@')[0], // Simple username from email
          is_staff: decoded.is_staff,
        });
      } catch (error) {
        console.error('Error decoding token:', error);
        logout();
        navigate('/');
      }
    } else {
      logout();
      navigate('/');
    }
    setLoading(false);
  }, [navigate, logout]);

  // Add this useEffect to handle initial navigation
  useEffect(() => {
    if (location.pathname === '/AdminDashboard') {
      navigate('/AdminDashboard/dashboard');
    }
  }, [location.pathname, navigate]);

  const renderContent = () => {
    switch (location.pathname) {
      case "/AdminDashboard/dashboard":
        return <Dashboard userData={userData} />;
      case "/AdminDashboard/settings/users":
        return <Users />;
      case "/AdminDashboard/settings/dumps":
        return <Dumps />;
      case "/AdminDashboard/employees":
        return <Employee />;
      case "/AdminDashboard/bills":
        return <Bill />;
      case "/AdminDashboard/platforms":
        return <Platforms />;
      case "/AdminDashboard/customers":
        return <Customers />;
      case "/AdminDashboard/bookings":
        return <Bookings />;
      case "/AdminDashboard/todos":
        return <Todos />;
      case "/AdminDashboard/alloggiati":
        return <Alloggiati />;  
      case "/AdminDashboard/certificati":
        return <Certificati />;
      case "/AdminDashboard/imposta-soggiorno":
        return <ImpostaSoggiorno />;
      case "/AdminDashboard/media":
        return <MediaManager />;
      case "/AdminDashboard/airbnb-calendar":
        return <AirBNBCalendar />;
      default:
        return <Dashboard userData={userData} />;
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const handleMenuClick = (key) => {
    navigate(key);
  };

  return (
    <Layout style={{ minHeight: "100vh", height: "100vh", overflow: "hidden" }}>
      <Header
        style={{
          padding: "0 16px",
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: "16px", width: 64, height: 64 }}
          />
          <Logo.Favicon 
            size={20}
            showText={false}
            level={5}
            style={{ marginRight: 8 }}
            hyperlink={true}
          />
          <Typography.Title level={4} style={{ margin: 0 }}>
            Admin Dashboard
          </Typography.Title>
        </Space>
        <UserMenu />
      </Header>

      <Layout>
        <Sider
          width={200}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth={screens.xs ? 0 : 80}
          trigger={null} 
          onBreakpoint={(broken) => {
            setCollapsed(broken);
          }}
          style={{
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 64px)",
            overflow: "hidden",
          }}
        >

          <Menu
            mode="inline"
            defaultSelectedKeys={[location.pathname === '/AdminDashboard' ? '/AdminDashboard/dashboard' : location.pathname]}
            selectedKeys={[location.pathname === '/AdminDashboard' ? '/AdminDashboard/dashboard' : location.pathname]}
            onClick={({ key }) => handleMenuClick(key)}
            style={{
              borderRight: 0,
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: 'calc(100vh - 140px)',
            }}
          >
            {/* Core Operations */}
              <Menu.Item
                key="/AdminDashboard/dashboard"
                icon={<DashboardOutlined />}
                onClick={() => navigate("/AdminDashboard/dashboard")}
              >
                Dashboard 
              </Menu.Item>

              <Menu.Item 
                key="/AdminDashboard/bookings" 
                icon={<CalendarOutlined />}
              >
                Bookings
              </Menu.Item>

              <Menu.Item 
                key="/AdminDashboard/bills" 
                icon={<WalletOutlined />}
              >
                Bills
              </Menu.Item>

              <Menu.Item 
                key="/AdminDashboard/todos" 
                icon={<CheckSquareOutlined />}
              >
                Todos
              </Menu.Item>



            {/* Sales & Distribution */}
            <CustomItemGroup title="Sales & Distribution" collapsed={collapsed}>
              <Menu.Item 
                key="/AdminDashboard/platforms" 
                icon={<ApiOutlined />}
              >
                Platforms
              </Menu.Item>
              
              <Menu.Item 
                key="/AdminDashboard/airbnb-calendar" 
                icon={<CalendarOutlined />}
              >
                Airbnb Calendar
              </Menu.Item>
            </CustomItemGroup>

            {/* Compliance & Legal */}
            <CustomItemGroup title="Compliance & Legal" collapsed={collapsed}>
              <Menu.Item 
                key="/AdminDashboard/alloggiati" 
                icon={<FormOutlined />}
              >
                Alloggiati
              </Menu.Item>
              <Menu.Item
                key="/AdminDashboard/certificati"
                icon={<SafetyCertificateOutlined />}
              >
                Certificati
              </Menu.Item>
              <Menu.Item
                key="/AdminDashboard/imposta-soggiorno"
                icon={<EuroCircleOutlined />}
              >
                Imposta di Soggiorno
              </Menu.Item>
            </CustomItemGroup>

            {/* Content Management */}
            <CustomItemGroup title="Content Management" collapsed={collapsed}>
              <Menu.Item 
                key="/AdminDashboard/media" 
                icon={<FileOutlined />}
              >
                Media Manager
              </Menu.Item>
            </CustomItemGroup>

            {/* Settings & Administration */}
            <CustomItemGroup title="Settings & Administration" collapsed={collapsed}>
              <Menu.Item 
                key="/AdminDashboard/settings/users" 
                icon={<UserOutlined />}
              >
                Users
              </Menu.Item>
              
              <Menu.Item 
                key="/AdminDashboard/settings/dumps" 
                icon={<DatabaseOutlined />}
              >
                Dumps
              </Menu.Item>
            </CustomItemGroup>
          </Menu>
        </Sider>
        <Layout
          style={{
            padding: screens.md ? "24px" : "12px",
            height: "98%",
            overflow: "auto", 
          }}
        >
          <Content style={{ height: "100%" }}>{renderContent()}</Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default AdminDashboard;
